import { useTranslation } from "react-i18next";
import { formatCurrency } from "../../utils/currency";
import { formatPercentDisplay } from "../../utils/numberFormat";
import { NumericInput } from "../atoms/NumericInput";
import type { OrderFormDiscountType, OrderTotalsResult } from "../../utils/orderTotals";
import type { OrderDiscountScope } from "../../types/models";

export interface OrderDiscountControlsProps {
  discountScope: OrderDiscountScope;
  /** Handler should clear the other mode's inputs when switching (Story 11.4-1). */
  onScopeChange: (scope: OrderDiscountScope) => void;
  orderDiscountType: OrderFormDiscountType;
  onOrderDiscountTypeChange: (type: OrderFormDiscountType) => void;
  orderDiscountValue: number;
  onOrderDiscountValueChange: (value: number) => void;
  discountReason: string;
  onDiscountReasonChange: (value: string) => void;
  totals: OrderTotalsResult;
  /** data-testid prefix, e.g. "create" | "edit" | "quicksale". */
  idPrefix?: string;
  /**
   * Which part to render:
   * - "selector" (default): the discount-mode selector (None / Per-line / Whole-order) — render ABOVE
   *   the lines so the operator picks the mode first (per-line controls then appear on each line).
   * - "summary": the whole-order discount control + reason (only in "order" mode, since it is global)
   *   followed by the live subtotal/discount/total — render BELOW the lines.
   */
  section?: "selector" | "summary";
}

/**
 * Story 11.4-1 — the discount-mode selector renders above the line items; the whole-order (global)
 * discount control + reason and the live totals render below the lines. Reused by Create/Edit + Quick Sale.
 */
export function OrderDiscountControls({
  discountScope,
  onScopeChange,
  orderDiscountType,
  onOrderDiscountTypeChange,
  orderDiscountValue,
  onOrderDiscountValueChange,
  discountReason,
  onDiscountReasonChange,
  totals,
  idPrefix = "order",
  section = "selector",
}: OrderDiscountControlsProps) {
  const { t } = useTranslation("orders");

  const modes: { value: OrderDiscountScope; label: string }[] = [
    { value: "none", label: t("form_discount_mode_none") },
    { value: "line", label: t("form_discount_mode_line") },
    { value: "order", label: t("form_discount_mode_order") },
  ];
  const typeOptions: { value: OrderFormDiscountType; label: string }[] = [
    { value: "none", label: t("form_discount_mode_none") },
    { value: "percentage", label: t("form_discount_type_percentage") },
    { value: "fixed", label: t("form_discount_type_fixed") },
  ];

  // ── Above the lines: only the mode selector ──────────────────────────────
  if (section === "selector") {
    return (
      <div className="w-full" data-testid={`${idPrefix}-discount-controls`}>
        <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
          {t("form_discount_mode")}
        </label>
        <div className="flex rounded-lg overflow-hidden border border-neutral-300 dark:border-neutral-600 w-full max-w-md">
          {modes.map((m) => (
            <button
              key={m.value}
              type="button"
              onClick={() => onScopeChange(m.value)}
              aria-pressed={discountScope === m.value}
              className={`flex-1 px-3 py-2 text-sm font-medium min-h-[44px] border-l first:border-l-0 border-neutral-300 dark:border-neutral-600 ${
                discountScope === m.value
                  ? "bg-primary-600 text-white"
                  : "bg-white dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-600"
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ── Below the lines: the whole-order (global) discount + reason, then the totals ──
  if (discountScope === "none") return null;
  return (
    <div className="w-full space-y-3">
      {discountScope === "order" && (
        <div
          className="flex flex-col gap-3 sm:flex-row sm:items-end sm:gap-4"
          data-testid={`${idPrefix}-order-discount`}
        >
          <div>
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              {t("form_order_discount")}
            </label>
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex rounded-lg overflow-hidden border border-neutral-300 dark:border-neutral-600">
                {typeOptions.map((o) => (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => onOrderDiscountTypeChange(o.value)}
                    aria-pressed={orderDiscountType === o.value}
                    className={`px-3 py-2 text-sm font-medium min-h-[44px] border-l first:border-l-0 border-neutral-300 dark:border-neutral-600 ${
                      orderDiscountType === o.value
                        ? "bg-primary-600 text-white"
                        : "bg-white dark:bg-neutral-700 text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-600"
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
              {orderDiscountType !== "none" && (
                <NumericInput
                  min={0}
                  step="0.01"
                  value={orderDiscountValue}
                  onValueChange={(v) => onOrderDiscountValueChange(Math.max(0, v))}
                  className="w-28 text-center py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 min-h-[44px]"
                  aria-label={t("form_order_discount")}
                  data-testid={`${idPrefix}-order-discount-value`}
                />
              )}
            </div>
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
              {t("form_discount_reason")}
            </label>
            <input
              type="text"
              value={discountReason}
              onChange={(e) => onDiscountReasonChange(e.target.value)}
              placeholder={t("form_discount_reason_placeholder")}
              maxLength={500}
              className="w-full px-3 py-2 border border-neutral-300 dark:border-neutral-600 rounded-lg bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 min-h-[44px]"
            />
          </div>
        </div>
      )}

      <div
        className="rounded-lg border border-neutral-200 dark:border-neutral-700 bg-neutral-50 dark:bg-neutral-800/50 px-4 py-3 space-y-1"
        data-testid={`${idPrefix}-discount-summary`}
      >
        <div className="flex justify-between text-sm text-neutral-700 dark:text-neutral-300">
          <span>{t("form_subtotal")}</span>
          <span>{formatCurrency(totals.subtotal)}</span>
        </div>
        <div className="flex justify-between text-sm text-neutral-700 dark:text-neutral-300">
          <span>
            {t("form_summary_discount")}
            {discountScope === "order" && orderDiscountType === "percentage" && orderDiscountValue > 0
              ? ` (${formatPercentDisplay(orderDiscountValue)})`
              : ""}
          </span>
          <span>
            −{formatCurrency(discountScope === "order" ? totals.discountAmount : totals.subtotal - totals.total)}
          </span>
        </div>
        <div className="flex justify-between text-base font-semibold text-neutral-900 dark:text-neutral-100 pt-1 border-t border-neutral-200 dark:border-neutral-700">
          <span>{t("form_summary_total")}</span>
          <span>{formatCurrency(totals.total)}</span>
        </div>
      </div>
    </div>
  );
}
